import {
  Button, Chip, Dialog, DialogContent,
} from '@mui/material'
import { notify } from 'helpers/notify'
import useQuery from 'hooks/useQuery'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { io } from 'socket.io-client'
import getSocketUrl from 'helpers/socketUrl'
import { AuthContext } from './AuthContext'
import { reloadTableContext } from './ReloadTableContext'
import { toastContext } from './ToastContext'

const supportRequestEventName = 'supportRequest'
const joinRequestEventName = 'joinRequest'
const joinRequestResponseEventName = 'joinRequestResponse'
const translatorPoolEventName = 'translatorPool'
const pendingRequestsEventName = 'pendingRequests'

export const MediatorContext = createContext({
  clients: {},
  fetchClients: async () => {},
  translators: {},
  fetchTranslators: async () => {},
  pendingRequests: {},
  setPendingRequests: () => {},
})

const MediatorProvider = ({ children }) => {
  const navigate = useNavigate()
  const { setToastOptions } = useContext(toastContext)
  const { user } = useContext(AuthContext)
  const {
    data: translators,
    makeRequest: fetchTranslators,
    setData: setTranslators,
  } = useQuery({
    defaultEndpoint: '/mediators/translators',
  })
  const { data: clients, makeRequest: fetchClients } = useQuery({
    defaultEndpoint: '/mediators/clients',
  })
  const [pendingRequests, setPendingRequests] = useState([])

  const [requestingClient, setRequestingClient] = useState(null)
  const [joinRequests, setJoinRequests] = useState([])
  const [ws, setWs] = useState(null)

  const fetchPendingRequests = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_DOMAIN}/mediators/pendingRequests`, {
        credentials: 'include',
        headers: {
          authorization: `Bearer ${user.accessToken}`,
        },
      })
      const json = await res.json()
      if (res.ok && Array.isArray(json)) {
        setPendingRequests(json)
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log({ mediatorPendingRequestSyncErr: err?.message || err })
    }
  }, [user?.accessToken])

  useEffect(() => {
    const isCanceledRequestBeingHandled = (accountId) => {
      return (
        window.location.pathname.substring(
          window.location.pathname.length - accountId.length,
        ) === accountId
      )
    }
    const ws = io(getSocketUrl(), {
      auth: {
        token: `Bearer ${user.accessToken}`,
      },
      withCredentials: true,
      transports: ['websocket', 'polling'],
    })
    const pingInterval = setInterval(() => {
      ws.emit('ping')
    }, 25_000)
    ws.on('connect', fetchPendingRequests)
    ws.on(pendingRequestsEventName, (data) => {
      setPendingRequests(data)
    })
    ws.on(translatorPoolEventName, (data) => {
      setTranslators((ps) => {
        const currentTranslator = ps.translators.find(
          (t) => t.id === data.translatorId,
        )
        return {
          ...ps,
          translators: ps.translators.map((t) => {
            if (t.id !== data.translatorId) return t
            return {
              ...t,
              status: data.status,
              firstName: data.firstName ?? currentTranslator?.firstName,
              lastName: data.lastName ?? currentTranslator?.lastName,
              speciality: data.speciality ?? currentTranslator?.speciality,
            }
          }),
        }
      })
    })
    ws.on(supportRequestEventName, (data) => {
      if (data?.requestCanceled) {
        setRequestingClient(null)
        setToastOptions({
          message: `El cliente ${data.firstName} ${data.lastName} de ${data.companyName} ha cancelado su petición`,
          severity: 'info',
        })

        setPendingRequests((ps) => {
          return ps.filter((pr) => pr.id !== data.id)
        })

        const canceledRequestBeingHandled = isCanceledRequestBeingHandled(
          data.id,
        )
        if (canceledRequestBeingHandled) navigate('/mediators')
      } else {
        const { requestCanceled, ...pendingRequest } = data
        setPendingRequests((ps) => {
          const newPrs = [...ps]
          const foundRequestIndex = ps.findIndex(
            (pr) => pr.id === pendingRequest.id,
          )
          if (foundRequestIndex !== -1) { newPrs[foundRequestIndex] = pendingRequest } else newPrs.push(pendingRequest)
          return newPrs
        })
        setRequestingClient(pendingRequest)
        fetchPendingRequests()
      }
    })
    ws.on(joinRequestEventName, (data) => {
      setJoinRequests((ps) => {
        const newJrs = [...ps]

        const foundJrIndex = newJrs.findIndex(
          (jr) => jr.connectionId === data.connectionId,
        )

        if (foundJrIndex !== -1) newJrs[foundJrIndex] = data
        else newJrs.push(data)

        return newJrs
      })
    })
    setWs(ws)
    fetchPendingRequests()
    window.addEventListener('focus', fetchPendingRequests)
    return () => {
      window.removeEventListener('focus', fetchPendingRequests)
      clearInterval(pingInterval)
      ws.off('connect', fetchPendingRequests)
      ws.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, fetchPendingRequests])

  const contextValue = useMemo(
    () => ({
      ws,
      translators: translators?.translators || [],
      fetchTranslators,
      clients: clients?.clients || [],
      fetchClients,
      pendingRequests: pendingRequests || [],
      setPendingRequests,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translators, clients, pendingRequests],
  )
  return (
    <MediatorContext.Provider value={contextValue}>
      {requestingClient && (
        <NewRequestModal
          requestingClient={requestingClient}
          setRequestingClient={setRequestingClient}
        />
      )}
      {joinRequests.map((jr) => (
        <NewJoinRequestModal
          key={jr.connectionId}
          joinRequest={jr}
          setJoinRequests={setJoinRequests}
        />
      ))}
      <Outlet />
    </MediatorContext.Provider>
  )
}

export default MediatorProvider

const NewRequestModal = ({ requestingClient, setRequestingClient }) => {
  const { pathname } = useLocation()
  const { reloadTable } = useContext(reloadTableContext)

  useEffect(() => {
    const audioNotificationClearer = notify({
      title: 'Nueva petición',
      message: 'Ha llegado una petición',
    })
    return () => {
      audioNotificationClearer()
      setRequestingClient(null)
      if (pathname === '/mediators') reloadTable()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Dialog open>
      <DialogContent
        className='flex flex-col items-center gap-4'
        style={{ padding: 'max(2vw, 2vh) max(3vw, 3vh)' }}
      >
        <h2 className='flex items-center gap-2'>
          <span>El cliente</span>
          {' '}
          <span>
            <strong>{`${requestingClient.firstName} ${requestingClient.lastName}`}</strong>
          </span>
          {' '}
          <span>esta solicitando ayuda</span>
        </h2>
        <Button onClick={() => setRequestingClient(null)}>Cerrar</Button>
      </DialogContent>
    </Dialog>
  )
}

const NewJoinRequestModal = ({
  joinRequest: {
    connectionId, name, dni, email, telephone,
  },
  setJoinRequests,
}) => {
  const { ws } = useContext(MediatorContext)

  const resultHandler = async (approved) => {
    ws.emit(joinRequestResponseEventName, approved, connectionId)
    setJoinRequests((ps) => ps.filter((jr) => jr.connectionId !== connectionId))
  }

  useEffect(() => {
    const audioNotificationClearer = notify({
      title: 'Nueva petición de invitado',
      message: 'Ha llegado una petición de invitado',
    })
    return () => {
      audioNotificationClearer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dataForClipboard = `${name}\t${email}\t${dni}\t${telephone}`

  return (
    <Dialog PaperProps={{ style: { maxWidth: 'unset' } }} open>
      <div
        className='flex flex-col items-center gap-4'
        style={{ padding: 'max(2vw, 2vh) max(3vw, 3vh)' }}
      >
        <h2 className='flex items-center gap-2'>
          <span>El invitado</span>
          {' '}
          <span>
            <strong>{name}</strong>
          </span>
          {' '}
          <span>esta solicitando entrar a una reunión</span>
        </h2>
        <div className='flex flex-col flex-wrap justify-center items-center gap-4 p-4'>
          <div className='flex gap-2 flex-wrap'>
            {email && <LabelChip label='Email' info={email} />}
            {telephone && <LabelChip label='Teléfono' info={telephone} />}
            {dni && <LabelChip label='DNI' info={dni} />}
          </div>
          <Button
            color='quaternary'
            style={{ fontSize: '12px', padding: '3px 10px' }}
            onClick={() => navigator.clipboard.writeText(dataForClipboard)}
          >
            Copiar
          </Button>
        </div>
        <div className='flex items-center gap-4'>
          <Button onClick={() => resultHandler(false)}>Desaprobar</Button>
          <Button onClick={() => resultHandler(true)}>Aprobar</Button>
        </div>
      </div>
    </Dialog>
  )
}

const LabelChip = ({ label, info }) => {
  return (
    <div className='flex flex-col gap-2 p-[10px] rounded shadow-sm bg-[var(--color-primary)]'>
      <p className='block text-gray-700 text-sm font-bold'>{label}</p>
      <Chip label={info} />
    </div>
  )
}
